import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  subscribeAdditionalService,
  unsubscribeAdditionalService,
} from "@services/additional-services/services";
import {
  type IAdditionalService,
  type IOrderedAdditionalServiceData,
} from "@services/additional-services/types";
import { type IOverviewJointBillService } from "@services/overview/types";

export const useSubscribeAdditionalServiceMutation = (
  serviceId: IAdditionalService["additionalServiceId"],
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jointBillServiceId: number) =>
      subscribeAdditionalService(serviceId, jointBillServiceId),
    onSuccess: async (_, jointBillServiceId) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["additionalService", serviceId],
        }),
        queryClient.invalidateQueries({
          queryKey: ["jointBillServicePeriodData", jointBillServiceId],
        }),
      ]);
    },
  });
};

export const useUnsubscribeAdditionalServiceMutation = (
  serviceId: IAdditionalService["additionalServiceId"],
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      jointBillServiceId,
      jointBillServiceAgreementId,
    }: {
      jointBillServiceId: IOverviewJointBillService["jointBillServiceId"];
      jointBillServiceAgreementId?: IOrderedAdditionalServiceData["jointBillServiceAgreementId"];
    }) =>
      unsubscribeAdditionalService(
        serviceId,
        jointBillServiceId,
        jointBillServiceAgreementId,
      ),
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["additionalService", serviceId],
        }),
        queryClient.invalidateQueries({
          queryKey: [
            "jointBillServicePeriodData",
            variables.jointBillServiceId,
          ],
        }),
      ]);
    },
  });
};
